import {reposWebApi} from "./customerAPI";
import secureLocalStorage from "react-secure-storage";
import {refreshToken} from "./sessionAction";

// Action Types
export const RFID_LOADING = 'RFID_LOADING';
export const RFID_LOADING_FALSE = 'RFID_LOADING_FALSE';
export const RFID_ERROR = 'RFID_ERROR';
export const GET_RFID_TAGS = 'GET_RFID_TAGS';
export const GET_RFID_TAG_HISTORY = 'GET_RFID_TAG_HISTORY';
export const RFID_TAG_ACTIVATED = 'RFID_TAG_ACTIVATED';
export const RFID_TAG_DEACTIVATED = 'RFID_TAG_DEACTIVATED';
export const RFID_TAG_UPDATED = 'RFID_TAG_UPDATED';
export const RFID_TAG_DELETED = 'RFID_TAG_DELETED';
export const GET_RFID_STATUS_LIST = 'GET_RFID_STATUS_LIST';
export const GET_SUB_ASSETS_FOR_MAP = 'GET_SUB_ASSETS_FOR_MAP';
export const GET_RFID_FOR_MAP = 'GET_RFID_FOR_MAP';
export const GET_SUB_ASSET_RFID_TAGS = 'GET_SUB_ASSET_RFID_TAGS';
export const CLEAR_RFID_DATA = 'CLEAR_RFID_DATA';
export const GET_RFID_COUNT = 'GET_RFID_COUNT';

const getAuthHeaders = () => ({
    Authorization: 'Bearer ' + secureLocalStorage.getItem("access_token")
});

// Helper for error handling
const handleApiError = async (error, dispatch, enqueueSnackbar, asyncAction) => {
    // Set default error message
    let errorMessage = 'An error occurred. Please try again.';
    
    // Handle different error types
    if (error?.response) {
        // Server responded with an error status
        const status = error.response.status;
        
        if (status === 401) {
            try {
                await dispatch(refreshToken());
                // Retry original action if provided
                if (asyncAction) {
                    return await asyncAction();
                }
            } catch (refreshError) {
                errorMessage = 'Your session has expired. Please log in again.';
                dispatch({type: RFID_ERROR, payload: refreshError});
                if (enqueueSnackbar) enqueueSnackbar(errorMessage, {variant: 'error'});
                throw refreshError;
            }
        } else if (status === 502) {
            errorMessage = 'Server is temporarily unavailable. Please try again later.';
        } else if (status === 500) {
            errorMessage = 'Internal server error. Our team has been notified.';
        } else if (status === 404) {
            errorMessage = 'The requested resource was not found.';
        } else if (status >= 400 && status < 500) {
            // Client errors
            errorMessage = error.response.data?.data || error.response.data?.error || error.response.data?.message || `Request error (${status})`;
        } else {
            // Other server errors
            errorMessage = `Server error (${status}). Please try again later.`;
        }
    } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'No response from server. Please check your connection.';
    } else {
        // Something else happened while setting up the request
        errorMessage = error.message || 'An unexpected error occurred.';
    }
    
    // Dispatch error action and show notification
    dispatch({type: RFID_ERROR, payload: { ...error, message: errorMessage }});
    if (enqueueSnackbar) enqueueSnackbar(errorMessage, {variant: 'error'});
}

// Get RFID Status List
export const getRfidStatusList = (params, enqueueSnackbar) => async (dispatch) => {
    try {
        const res = await reposWebApi.get('/tracker/list/rfid_status', {
            params,
            headers: getAuthHeaders()
        });
        dispatch({
            type: GET_RFID_STATUS_LIST,
            payload: res.data.data,
        });
    } catch (error) {
        await handleApiError(error, dispatch, enqueueSnackbar, () => getRfidStatusList(params, enqueueSnackbar));
        throw error;
    } finally {
        dispatch({type: RFID_LOADING_FALSE});
    }
}

// Get RFID Tag History
export const getRfidTagHistory = (rfid, enqueueSnackbar) => async (dispatch) => {
    try {
        dispatch({type: RFID_LOADING});
        const res = await reposWebApi.get('/tracker/list/rfid_history', {
            params: {rfid:rfid},
            headers: getAuthHeaders()
        });
        dispatch({
            type: GET_RFID_TAG_HISTORY,
            payload: res.data.data
        });
    } catch (error) {
        await handleApiError(error, dispatch, enqueueSnackbar, () => getRfidTagHistory(rfid, enqueueSnackbar));
        // Re-throw to allow handling in the component
    } finally {
        dispatch({type: RFID_LOADING_FALSE});
    }
};

// Activate/Deactivate RFID Tag
export const manageRfidTag = (rfid, isActivate, enqueueSnackbar) => async (dispatch) => {
    try {
        dispatch({type: RFID_LOADING});
        const body = {rfid, is_activate: isActivate ? '1' : '0'};
        await reposWebApi.post('/tracker/manage/rfidtag', body, {
            headers: {...getAuthHeaders(), 'Content-Type': 'multipart/form-data'}
        });
        dispatch({
            type: isActivate ? RFID_TAG_ACTIVATED : RFID_TAG_DEACTIVATED,
            payload: {rfid, status: isActivate ? 1 : 0}
        });
        if (enqueueSnackbar) enqueueSnackbar(
            isActivate ? 'RFID tag activated' : 'RFID tag deactivated',
            {variant: 'success'}
        );
    } catch (error) {
        handleApiError(error, dispatch, enqueueSnackbar);
    } finally {
        dispatch({type: RFID_LOADING_FALSE});
    }
};

// Update (Replace) RFID Tag
export const updateRfidTag = (oldRfid, newRfid, enqueueSnackbar) => async (dispatch) => {
    try {
        dispatch({type: RFID_LOADING});
        const body = {rfid: oldRfid, new_rfid: newRfid};
        
        const response = await reposWebApi.put('/tracker/manage/rfidtag', body, {
            headers: {...getAuthHeaders(), 'Content-Type': 'multipart/form-data'}
        });
        
        dispatch({type: RFID_TAG_UPDATED, payload: {oldRfid, newRfid}});
        
        if (enqueueSnackbar) {
            enqueueSnackbar('RFID tag updated', {variant: 'success'});
        }

        return Promise.resolve({
            success: true,
            data: response.data,
            message: response?.data?.data || 'RFID tag updated successfully'
        });
    } catch (error) {
        handleApiError(error, dispatch, enqueueSnackbar, () => updateRfidTag(oldRfid, newRfid, enqueueSnackbar));
        return Promise.reject({
            success: false,
            error,
            message: error.response?.data || 'Failed to update RFID tag'
        });
    } finally {
        dispatch({type: RFID_LOADING_FALSE});
    }
};

// Delete RFID Tag
export const deleteRfidTag = (rfid, enqueueSnackbar) => {
    return async (dispatch) => {
        try {
            dispatch({type: RFID_LOADING});
            const response = await reposWebApi.delete('/tracker/manage/rfidtag', {
                data: {rfid},
                headers: getAuthHeaders()
            });
            
            dispatch({type: RFID_TAG_DELETED, payload: {rfid}});
            
            if (enqueueSnackbar) {
                enqueueSnackbar(response?.data?.data || 'RFID tag deleted', {variant: 'success'});
            }
            
            return Promise.resolve({
                success: true,
                data: response.data,
                message: response?.data?.data || 'RFID tag deleted successfully'
            });
        } catch (error) {
            await handleApiError(error, dispatch, enqueueSnackbar);
            return Promise.reject({
                success: false,
                error,
                message: error.response?.data?.data || 'Failed to delete RFID tag'
            });
        } finally {
            dispatch({type: RFID_LOADING_FALSE});
        }
    };
};

// List Sub-Assets for Map
export const getSubAssetsForMap = (params = {}, enqueueSnackbar) => async (dispatch) => {
    try {
        dispatch({type: RFID_LOADING});
        const res = await reposWebApi.get('/tracker/list/sub-asset_for_map', {
            params,
            headers: getAuthHeaders()
        });
        dispatch({type: GET_SUB_ASSETS_FOR_MAP, payload: res.data.data});
    } catch (error) {
        handleApiError(error, dispatch, enqueueSnackbar, () => getSubAssetsForMap(params, enqueueSnackbar));
    } finally {
        dispatch({type: RFID_LOADING_FALSE});
    }
};

// List RFID Tags for Mapping
export const getRfidTagsForMap = (params = {}, enqueueSnackbar) => async (dispatch) => {
    try {
        dispatch({type: RFID_LOADING});
        
        const res = await reposWebApi.get('/tracker/list/rfid_for_map', {
            params,
            headers: getAuthHeaders()
        });
        
        if (res && res.data && res.data.data) {
            dispatch({type: GET_RFID_FOR_MAP, payload: res.data.data});
            return res.data.data;
        }
    } catch (error) {
        console.error("Error fetching RFID tags for map:", error);
        handleApiError(error, dispatch, enqueueSnackbar);
        
        // Don't recursively call the same function as it could lead to infinite loops
        // Instead, return a rejected promise
        return Promise.reject(error);
    } finally {
        dispatch({type: RFID_LOADING_FALSE});
    }
};

// Get Sub-Asset RFID Tags (new API)
export const getSubAssetRfidTags = (params = {}, enqueueSnackbar) => async (dispatch) => {
    try {
        dispatch({type: RFID_LOADING});
        const res = await reposWebApi.get('/tracker/list/sub-asset', {
            params,
            headers: getAuthHeaders()
        });
        dispatch({type: GET_SUB_ASSET_RFID_TAGS, payload: res.data.data});
    } catch (error) {
        await handleApiError(error, dispatch, enqueueSnackbar, () => getSubAssetRfidTags(params, enqueueSnackbar));
        throw error;
    } finally {
        dispatch({type: RFID_LOADING_FALSE});
    }
};

// Add (Activate) RFID Tag
export const addRfidTag = (rfid, sub_asset_id, enqueueSnackbar) => async (dispatch) => {
    try {
        dispatch({type: RFID_LOADING});
        const body = {rfid, sub_asset_id};
        
        const response = await reposWebApi.post('/tracker/add/rfid', body, {
            headers: getAuthHeaders()
        });
        
        dispatch({type: RFID_TAG_ACTIVATED, payload: {rfid, sub_asset_id, status: 1}});
        
        if (enqueueSnackbar) {
            enqueueSnackbar(response?.data?.data || 'RFID tag added and activated', {variant: 'success'});
        }
        
        return Promise.resolve({
            success: true,
            data: response.data,
            message: 'RFID tag added and activated successfully'
        });
    } catch (error) {
        handleApiError(error, dispatch, enqueueSnackbar, () => addRfidTag(rfid, sub_asset_id, enqueueSnackbar));
        return Promise.reject({
            success: false,
            error,
            message: error.response?.data?.data || 'Failed to add RFID tag'
        });
    } finally {
        dispatch({type: RFID_LOADING_FALSE});
    }
};

export const getRfidCount = (params, enqueueSnackbar) => async (dispatch) => {
    try {
        const res = await reposWebApi.get('/tracker/get_rfid_count', {
            headers: getAuthHeaders()
        });
        dispatch({
            type: GET_RFID_COUNT,
            payload: res.data,
        });
    } catch (error) {
        await handleApiError(error, dispatch, enqueueSnackbar, () => getRfidCount(params, enqueueSnackbar));
        throw error;
    } finally {
        dispatch({type: RFID_LOADING_FALSE});
    }
}

// Clear all RFID data from state
export const clearRfidData = () => ({type: CLEAR_RFID_DATA});